import { Box, Card, CardContent, Typography } from '@mui/material';

import { env } from '@/shared/config/env';
import { useAuth } from '@/shared/auth/useAuth';
import { usePermissions } from '@/shared/permissions/usePermissions';
import { useTenant } from '@/shared/tenant/useTenant';
import { PageContainer } from '@/shared/ui/PageContainer';

interface InfoCardProps {
  label: string;
  value?: string | null;
  hint?: string | null;
}

function InfoCard({ label, value, hint }: InfoCardProps) {
  return (
    <Card>
      <CardContent>
        <Typography variant="overline" color="text.secondary">
          {label}
        </Typography>
        <Typography variant="h6" sx={{ mt: 0.5 }} noWrap>
          {value ?? '—'}
        </Typography>
        {hint ? (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
            {hint}
          </Typography>
        ) : null}
      </CardContent>
    </Card>
  );
}

/**
 * Placeholder home page.
 *
 * Contains no business logic. It demonstrates that the foundation wiring works
 * end-to-end by reading the tenant, auth, and permission contexts.
 */
export function HomePage() {
  const { user } = useAuth();
  const { tenantName, tenantId } = useTenant();
  const { permissions, hasPermission } = usePermissions();

  const canManageDelivery = hasPermission('settings.delivery.manage');

  return (
    <PageContainer
      title={`Welcome to ${env.appName}`}
    >
      <Box
        sx={{
          display: 'grid',
          gap: 2.5,
          gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' },
        }}
      >
        <InfoCard label="Active tenant" value={tenantName} hint={tenantId} />
        <InfoCard label="Signed in as" value={user?.name} hint={user?.email} />
        <InfoCard
          label="Permissions loaded"
          value={`${permissions.length} granted`}
          hint={`settings.delivery.manage: ${canManageDelivery ? 'granted' : 'denied'}`}
        />
      </Box>
    </PageContainer>
  );
}
